import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import OnClickOutside from 'sushiweb/HOC/OnClickOutside';
import coal from 'sushiweb/tokens/color/black';

import { ChevronDown } from 'lucide-react';

export const MenuWidget = ({
  picture,
  title,
  subtitle,
  initials,
  menuText,
  onMenuSelect,
  hideText,
  showArrow = true,
  distanceFromTop = '3rem',
  menuWidgetWidth = '16rem',
  headMaxWidth = '16rem',
  isOpenWidget = false,
  setOpenWidget = () => {},
}) => {
  const [isDropdownVisible, setDropDownVisible] = useState(false);
  const toggleDropDown = () => setDropDownVisible((prev) => !prev);
  const closeDropdown = () => {
    setDropDownVisible(false);
    setOpenWidget?.(false);
  };

  useEffect(() => {
    setDropDownVisible(isOpenWidget);
  }, [isOpenWidget]);

  const name = (
    <MenuWidget.Name hideText={hideText} data-dd-privacy='mask'>
      {title}
    </MenuWidget.Name>
  );

  return (
    <div>
      <OnClickOutside handleClickOutside={closeDropdown}>
        <MenuWidget.Head onClick={toggleDropDown} maxWidth={headMaxWidth}>
          {/* `picture` wins over `initials` — the initials circle is the
              fallback for callers that have no avatar image, never a second
              circle beside one. */}
          {picture ? <MenuWidget.Thumb src={picture} /> : null}
          {!picture && initials ? (
            <MenuWidget.Avatar data-dd-privacy='hidden'>
              {initials}
            </MenuWidget.Avatar>
          ) : null}
          {/* Only the subtitle variant needs the extra column wrapper; without
              a subtitle the head keeps the exact markup (and therefore the
              exact layout) every existing caller of this shared widget had. */}
          {subtitle ? (
            <MenuWidget.TextCol>
              {name}
              <MenuWidget.Subtitle data-dd-privacy='mask'>
                {subtitle}
              </MenuWidget.Subtitle>
            </MenuWidget.TextCol>
          ) : (
            name
          )}
          {menuText && showArrow ? (
            <StyledChevronDown
              size={18}
              visible={isDropdownVisible ? true : null}
            />
          ) : null}
        </MenuWidget.Head>

        {menuText && (
          <MenuWidget.Dropdown
            visible={isDropdownVisible}
            distanceFromTop={distanceFromTop}
            menuWidgetWidth={menuWidgetWidth}
          >
            <MenuWidget.Link onClick={onMenuSelect}>{menuText}</MenuWidget.Link>
          </MenuWidget.Dropdown>
        )}
      </OnClickOutside>
    </div>
  );
};

const StyledChevronDown = styled(ChevronDown)`
  transform: rotate(${(props) => (props.visible ? '180deg' : 0)});
  transition: transform 0.2s ease;
`;

MenuWidget.defaultProps = {
  picture: '',
  profile_link: '',
  title: '',
  menuText: '',
  hideText: true,
};

MenuWidget.Link = styled.div`
  font-size: 1.5rem;
  width: 100%;
  padding: 1rem;
  background: #fff;
  text-align: left;

  &:hover {
    background: '#E8E8E8';
  }
`;

MenuWidget.Dropdown = styled.div`
  position: absolute;
  top: ${(props) => props.distanceFromTop};
  right: 2.5rem;
  visibility: ${(props) => (props.visible ? 'visible' : 'hidden')};
  opacity: ${(props) => (props.visible ? 1 : 0)};
  width: ${(props) => props.menuWidgetWidth};
  background: #fff;
  border-radius: 6px;
  box-shadow: 0px 2px 8px rgba(28, 28, 28, 0.15);
  transform: translateY(${(props) => (props.visible ? '20px' : 0)});
  transition: transform 0.25s ease, opacity 0.25s ease;
  overflow: hidden;
  z-index: 3;
`;

MenuWidget.Head = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  max-width: ${(props) => props.maxWidth || '16rem'};
  cursor: pointer;
`;

MenuWidget.Name = styled.span`
  font-size: 14px;
  font-weight: 500;
  margin: 0 1rem 0 0;
  color: ${coal};

  ${({ hideText }) =>
    hideText &&
    `
    overflow: hidden;
    // max-width: 9rem;
    text-overflow: ellipsis;
    white-space: nowrap;
  `}
`;

MenuWidget.Thumb = styled.div.attrs({
  'data-dd-privacy': 'hidden',
})`
  width: 4rem;
  height: 4rem;
  flex-shrink: 0;
  margin-right: 1rem;
  border-radius: 50%;
  background-image: url(${(props) => props.src});
  background-repeat: no-repeat;
  background-position: center;
  background-size: cover;
`;

MenuWidget.TextCol = styled.div`
  display: flex;
  flex-direction: column;
  min-width: 0;
  justify-content: center;
`;

MenuWidget.Avatar = styled.div`
  width: 3.6rem;
  height: 3.6rem;
  flex-shrink: 0;
  border-radius: 50%;
  background: #e3e6eb;
  color: #44474e;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.4rem;
  font-weight: 600;
  margin-right: 1rem;
  text-transform: uppercase;
`;

MenuWidget.Subtitle = styled.span`
  font-size: 1.2rem;
  color: #7a7d85;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;
